import { useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { Clock, Filter, Plus, Trash2, Zap } from 'lucide-react'
import PageContainer from '@/components/layout/PageContainer'
import Card from '@/components/layout/Card'
import Select from '@/components/ui/Select'
import ActionStep from '@/features/workflows/ActionStep'
import { useWorkflowLookups } from '@/features/workflows/useWorkflowLookups'
import { useCreateWorkflow, useUpdateWorkflow, useWorkflows } from '@/features/workflows/useWorkflows'
import {
  CONDITION_FIELDS,
  CONDITION_OPS,
  TRIGGERS,
  delayOf,
  describeAction,
  describeCondition,
  describeTrigger,
  newAction,
} from '@/features/workflows/workflowMeta'
import { apiErrorMessage } from '@/features/settings/apiErrorMessage'
import { cn } from '@/lib/cn'
import type { Action, Condition, Workflow, WorkflowInput } from '@/api/workflows'

const inputCls =
  'w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand-primary/40'

interface Draft {
  name: string
  enabled: boolean
  triggerType: string
  stageId: string
  dateField: string
  offsetDays: string
  repeatYearly: boolean
  conditions: Condition[]
  actions: Action[]
}

function draftOf(w?: Workflow): Draft {
  const cfg = w?.triggerConfig ?? {}
  const rawDate = String(cfg.dateField ?? (w?.triggerType === 'contact_date_reached' ? 'birthday' : 'next_contact_date'))
  return {
    name: w?.name ?? '',
    enabled: w?.enabled ?? true,
    triggerType: w?.triggerType ?? 'deal_created',
    stageId: String(cfg.stageId ?? ''),
    dateField: rawDate.startsWith('custom.') ? rawDate.slice('custom.'.length) : rawDate,
    offsetDays: Number(cfg.offsetDays ?? 0) ? String(cfg.offsetDays) : '',
    repeatYearly: cfg.repeatYearly === true || cfg.repeatYearly === 'true',
    conditions: (w?.conditions ?? []).map((c) => ({ ...c })),
    actions: w ? w.actions.map((a) => ({ ...a })) : [newAction('create_task')],
  }
}

/** A labelled stop on the flow's vertical line. */
function FlowLabel({ icon, children }: { icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="flex items-center gap-2 py-2 pl-4">
      <span className="w-6 h-6 rounded-full bg-gray-100 text-gray-500 flex items-center justify-center">{icon}</span>
      <span className="text-xs font-medium text-gray-500">{children}</span>
    </div>
  )
}

/**
 * One workflow on its own page, laid out as it runs, top to bottom: what starts it, which deals it applies
 * to, then each step with any wait before it. Beside it, the same workflow in plain words — so it's never
 * unclear which workflow is open or what it will do.
 */
export default function WorkflowEditorPage() {
  const { id } = useParams()
  const isNew = !id
  const navigate = useNavigate()
  const { data: workflows, isLoading } = useWorkflows()
  const workflow = workflows?.find((w) => w.id === id)
  const { lookups, stages, templates, members, dealDateFields, contactDateFields } = useWorkflowLookups()
  const create = useCreateWorkflow()
  const update = useUpdateWorkflow()
  const save = isNew ? create : update

  const [draft, setDraft] = useState<Draft>(() => draftOf())
  const [baseline, setBaseline] = useState(() => JSON.stringify(draftOf()))
  const [loadedId, setLoadedId] = useState<string | null>(null)
  if (workflow && workflow.id !== loadedId) {
    setLoadedId(workflow.id)
    setDraft(draftOf(workflow))
    setBaseline(JSON.stringify(draftOf(workflow)))
  }
  const set = <K extends keyof Draft>(k: K, v: Draft[K]) => setDraft((d) => ({ ...d, [k]: v }))
  const dirty = JSON.stringify(draft) !== baseline

  useEffect(() => {
    if (!dirty) return
    const onBeforeUnload = (e: BeforeUnloadEvent) => e.preventDefault()
    window.addEventListener('beforeunload', onBeforeUnload)
    return () => window.removeEventListener('beforeunload', onBeforeUnload)
  }, [dirty])

  const isDate = draft.triggerType === 'date_reached' || draft.triggerType === 'contact_date_reached'
  const contactTrigger = draft.triggerType === 'contact_date_reached'
  const dateFields = contactTrigger ? contactDateFields : dealDateFields

  function triggerConfig(): Record<string, unknown> {
    if (draft.triggerType === 'deal_stage_changed' && draft.stageId) return { stageId: draft.stageId }
    if (isDate) return { dateField: draft.dateField, offsetDays: Number(draft.offsetDays || 0), repeatYearly: draft.repeatYearly }
    return {}
  }

  const problems = [
    !draft.name.trim() && 'Give the workflow a name.',
    draft.actions.length === 0 && 'Add at least one step.',
    draft.actions.some((a) => (a.type === 'send_email' || a.type === 'send_sms') && a.templateId === '') && 'Choose a template for each message step.',
    draft.actions.some((a) => a.type === 'notify' && !String(a.message ?? '').trim()) && 'Write the message for each “Notify” step.',
  ].filter(Boolean) as string[]

  function submit() {
    if (problems.length) return
    const input: WorkflowInput = {
      name: draft.name.trim(),
      enabled: draft.enabled,
      triggerType: draft.triggerType,
      triggerConfig: triggerConfig(),
      conditions: contactTrigger ? [] : draft.conditions,
      actions: draft.actions,
    }
    if (isNew) create.mutate(input, { onSuccess: (w) => { setBaseline(JSON.stringify(draft)); navigate(`/workflows/${w.id}`, { replace: true }) } })
    else if (id) update.mutate({ id, input }, { onSuccess: (w) => { setDraft(draftOf(w)); setBaseline(JSON.stringify(draftOf(w))) } })
  }

  const patchCondition = (i: number, p: Partial<Condition>) => set('conditions', draft.conditions.map((c, j) => (j === i ? { ...c, ...p } : c)))
  const moveAction = (i: number, dir: -1 | 1) => {
    const j = i + dir
    if (j < 0 || j >= draft.actions.length) return
    const next = [...draft.actions]
    ;[next[i], next[j]] = [next[j], next[i]]
    set('actions', next)
  }

  if (!isNew && !isLoading && !workflow) {
    return (
      <PageContainer width="full">
        <p className="text-sm text-gray-500">
          This workflow doesn't exist or was deleted. <Link to="/workflows" className="text-brand-primary hover:underline">Back to workflows</Link>
        </p>
      </PageContainer>
    )
  }

  const preview = { triggerType: draft.triggerType, triggerConfig: triggerConfig() }

  return (
    <PageContainer width="full">
      <nav aria-label="Breadcrumb" className="flex items-center gap-1.5 text-sm text-gray-500 mb-2">
        <Link to="/workflows" className="hover:text-brand-primary">Workflows</Link>
        <span aria-hidden>/</span>
        <span className="text-gray-700 truncate">{isNew ? 'New workflow' : workflow?.name}</span>
      </nav>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <input
          value={draft.name}
          onChange={(e) => set('name', e.target.value)}
          placeholder="Workflow name — e.g. Just sold follow-up"
          aria-label="Workflow name"
          className="flex-1 min-w-64 max-w-xl text-2xl font-semibold text-brand-darkest bg-transparent border-b border-transparent hover:border-gray-200 focus:border-brand-primary focus:outline-none py-1"
        />
        <div className="flex items-center gap-2">
          <button
            type="button"
            role="switch"
            aria-checked={draft.enabled}
            onClick={() => set('enabled', !draft.enabled)}
            className={cn('inline-flex items-center gap-2 px-3 py-2 rounded-lg border text-sm font-medium', draft.enabled ? 'border-success/30 bg-success/10 text-success' : 'border-gray-200 text-gray-500')}
          >
            <span className={cn('w-2 h-2 rounded-full', draft.enabled ? 'bg-success' : 'bg-gray-300')} />
            {draft.enabled ? 'On' : 'Off'}
          </button>
          <button type="button" onClick={submit} disabled={save.isPending || problems.length > 0 || (!isNew && !dirty)} className="px-4 py-2 bg-brand-primary text-white rounded-lg text-sm font-medium hover:bg-brand-dark disabled:opacity-60">
            {save.isPending ? 'Saving…' : isNew ? 'Create workflow' : dirty ? 'Save changes' : 'Saved'}
          </button>
        </div>
      </div>
      {save.isError && <p className="text-danger text-sm mb-3">{apiErrorMessage(save.error)}</p>}
      {problems.length > 0 && draft.name.trim() !== '' && (
        <ul className="mb-3 text-xs text-amber-700 space-y-0.5">{problems.map((p) => <li key={p}>• {p}</li>)}</ul>
      )}

      <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_22rem]">
        <div className="min-w-0 max-w-3xl">
          <div className="rounded-xl border-2 border-brand-primary/30 bg-white">
            <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-100">
              <span className="w-8 h-8 rounded-lg bg-brand-primary text-white flex items-center justify-center"><Zap size={16} /></span>
              <span className="text-xs font-semibold text-gray-400 tracking-wider">WHEN</span>
              <Select
                size="md"
                aria-label="Trigger"
                value={draft.triggerType}
                onChange={(e) => {
                  const t = e.target.value
                  setDraft((d) => ({
                    ...d,
                    triggerType: t,
                    dateField: t === 'contact_date_reached' ? 'birthday' : t === 'date_reached' ? 'next_contact_date' : d.dateField,
                    actions: t === 'contact_date_reached' ? d.actions.filter((a) => a.type !== 'set_next_contact_date') : d.actions,
                  }))
                }}
                className="flex-1 min-w-0"
              >
                {TRIGGERS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </Select>
            </div>
            {(draft.triggerType === 'deal_stage_changed' || isDate) && (
              <div className="p-4 grid gap-3 sm:grid-cols-3">
                {draft.triggerType === 'deal_stage_changed' && (
                  <div className="sm:col-span-3">
                    <label className="block text-xs font-medium text-gray-500 mb-1">Stage</label>
                    <Select size="md" value={draft.stageId} onChange={(e) => set('stageId', e.target.value)} className="w-full">
                      <option value="">Any stage</option>
                      {stages.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                    </Select>
                  </div>
                )}
                {isDate && (
                  <>
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">Date</label>
                      <Select size="md" aria-label="Date field" value={draft.dateField} onChange={(e) => set('dateField', e.target.value)} className="w-full">
                        {dateFields.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </Select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">Days after it</label>
                      <input className={inputCls} type="number" min={0} value={draft.offsetDays} onChange={(e) => set('offsetDays', e.target.value)} placeholder="0 = on the day" />
                    </div>
                    <label className="flex items-center gap-2 text-sm text-gray-700 sm:mt-6">
                      <input type="checkbox" checked={draft.repeatYearly} onChange={(e) => set('repeatYearly', e.target.checked)} />
                      Every year
                    </label>
                  </>
                )}
              </div>
            )}
          </div>

          {!contactTrigger && (
            <>
              <div className="ml-8 h-4 border-l-2 border-gray-200" aria-hidden />
              <div className="rounded-xl border border-gray-200 bg-white">
                <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-100">
                  <span className="w-8 h-8 rounded-lg bg-gray-100 text-gray-500 flex items-center justify-center"><Filter size={16} /></span>
                  <span className="text-xs font-semibold text-gray-400 tracking-wider">ONLY IF</span>
                  <span className="text-sm text-gray-500">{draft.conditions.length === 0 ? 'Every deal — no conditions' : 'All of these are true'}</span>
                </div>
                <div className="p-4 space-y-2">
                  {draft.conditions.map((c, i) => (
                    <div key={i} className="grid gap-2 sm:grid-cols-[1fr_9rem_1fr_auto] items-center">
                      <Select size="md" aria-label="Condition field" value={c.field} onChange={(e) => patchCondition(i, { field: e.target.value, value: '' })}>
                        {CONDITION_FIELDS.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
                      </Select>
                      <Select size="md" aria-label="Comparison" value={c.op} onChange={(e) => patchCondition(i, { op: e.target.value })}>
                        {CONDITION_OPS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </Select>
                      {c.field === 'stageId' ? (
                        <Select size="md" aria-label="Stage" value={String(c.value ?? '')} onChange={(e) => patchCondition(i, { value: e.target.value })}>
                          <option value="">Choose…</option>
                          {stages.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                        </Select>
                      ) : c.field === 'status' ? (
                        <Select size="md" aria-label="Status" value={String(c.value ?? '')} onChange={(e) => patchCondition(i, { value: e.target.value })}>
                          <option value="">Choose…</option>
                          {['open', 'won', 'lost'].map((s) => <option key={s} value={s}>{s}</option>)}
                        </Select>
                      ) : (
                        <input className={inputCls} aria-label="Value" value={String(c.value ?? '')} placeholder={c.field === 'value' ? 'e.g. 1000000' : 'Value'} onChange={(e) => patchCondition(i, { value: e.target.value })} />
                      )}
                      <button type="button" onClick={() => set('conditions', draft.conditions.filter((_, j) => j !== i))} className="p-1.5 text-gray-400 hover:text-danger" title="Remove condition"><Trash2 size={15} /></button>
                    </div>
                  ))}
                  <button type="button" onClick={() => set('conditions', [...draft.conditions, { field: 'value', op: 'gte', value: '' }])} className="inline-flex items-center gap-1 text-sm font-medium text-brand-primary">
                    <Plus size={14} /> Add condition
                  </button>
                </div>
              </div>
            </>
          )}

          {draft.actions.map((a, i) => (
            <div key={i}>
              <div className="ml-8 border-l-2 border-gray-200">
                {delayOf(a) > 0 ? <FlowLabel icon={<Clock size={13} />}>Wait {delayOf(a)} day{delayOf(a) === 1 ? '' : 's'}</FlowLabel> : <div className="h-4" />}
              </div>
              <ActionStep
                index={i}
                action={a}
                onChange={(next) => set('actions', draft.actions.map((x, j) => (j === i ? next : x)))}
                onRemove={() => set('actions', draft.actions.filter((_, j) => j !== i))}
                onMove={(dir) => moveAction(i, dir)}
                isFirst={i === 0}
                isLast={i === draft.actions.length - 1}
                templates={templates}
                members={members}
                contactTrigger={contactTrigger}
              />
            </div>
          ))}
          <div className="ml-8 h-4 border-l-2 border-gray-200" aria-hidden />
          <button type="button" onClick={() => set('actions', [...draft.actions, newAction('send_email')])} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-dashed border-brand-primary/40 text-sm font-medium text-brand-primary hover:bg-brand-primary/5">
            <Plus size={15} /> Add a step
          </button>
        </div>

        <aside className="xl:sticky xl:top-6 self-start" aria-label="Summary">
          <Card title="In plain words">
            <p className="text-sm font-medium text-brand-darkest">{describeTrigger(preview, lookups)}</p>
            {!contactTrigger && draft.conditions.length > 0 && (
              <p className="text-sm text-gray-600 mt-1">…but only if {draft.conditions.map((c) => describeCondition(c, lookups)).join(' and ')}</p>
            )}
            <ol className="mt-3 space-y-2">
              {draft.actions.map((a, i) => (
                <li key={i} className="flex gap-2 text-sm text-gray-700">
                  <span className="w-5 h-5 shrink-0 rounded-full bg-brand-primary/10 text-brand-primary text-[11px] font-semibold flex items-center justify-center">{i + 1}</span>
                  <span>
                    {!contactTrigger && delayOf(a) > 0 && <span className="text-gray-500">after {delayOf(a)} day{delayOf(a) === 1 ? '' : 's'}: </span>}
                    {describeAction(a, lookups)}
                  </span>
                </li>
              ))}
            </ol>
            <p className={cn('text-xs mt-3', draft.enabled ? 'text-success' : 'text-gray-400')}>{draft.enabled ? 'On — runs automatically.' : 'Off — nothing runs until you turn it on.'}</p>
          </Card>
        </aside>
      </div>
    </PageContainer>
  )
}
